import { useState, useEffect } from 'react';
import api from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const GAME_TYPES = ['501', '301', '701', 'Cricket'];

interface Format {
  id: string;
  name: string;
  gameType: string;
  defaultLegs: number;
  doubleIn: boolean;
  doubleOut: boolean;
  displayOrder: number;
}

interface League {
  id: string;
  name: string;
  abbreviation: string | null;
  facebookUrl: string | null;
  discordUrl: string | null;
  whatsappUrl: string | null;
  isActive: boolean;
  formats: Format[];
}

export default function Leagues() {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // League modal state
  const [showLeagueModal, setShowLeagueModal] = useState(false);
  const [editingLeague, setEditingLeague] = useState<League | null>(null);
  const [leagueForm, setLeagueForm] = useState({
    name: '',
    abbreviation: '',
    facebookUrl: '',
    whatsappUrl: '',
    discordUrl: ''
  });

  // Format modal state
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [editingFormat, setEditingFormat] = useState<Format | null>(null);
  const [formatLeagueId, setFormatLeagueId] = useState<string | null>(null);
  const [formatForm, setFormatForm] = useState({
    name: '',
    gameType: '501',
    defaultLegs: 5,
    doubleIn: false,
    doubleOut: true
  });

  // Expanded leagues state
  const [expandedLeagues, setExpandedLeagues] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchLeagues();
  }, []);

  const fetchLeagues = async () => {
    try {
      const response = await api.get('/leagues');
      setLeagues(response.data.leagues);
      setError('');
    } catch {
      setError('Failed to fetch leagues');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLeague = (leagueId: string) => {
    const newExpanded = new Set(expandedLeagues);
    if (newExpanded.has(leagueId)) {
      newExpanded.delete(leagueId);
    } else {
      newExpanded.add(leagueId);
    }
    setExpandedLeagues(newExpanded);
  };

  // League CRUD
  const openLeagueModal = (league: League | null = null) => {
    if (league) {
      setEditingLeague(league);
      setLeagueForm({
        name: league.name,
        abbreviation: league.abbreviation || '',
        facebookUrl: league.facebookUrl || '',
        whatsappUrl: league.whatsappUrl || '',
        discordUrl: league.discordUrl || ''
      });
    } else {
      setEditingLeague(null);
      setLeagueForm({
        name: '',
        abbreviation: '',
        facebookUrl: '',
        whatsappUrl: '',
        discordUrl: ''
      });
    }
    setShowLeagueModal(true);
  };

  const handleLeagueSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingLeague) {
        await api.put(`/leagues/${editingLeague.id}`, leagueForm);
        setSuccess('League updated');
      } else {
        await api.post('/leagues', leagueForm);
        setSuccess('League created');
      }
      fetchLeagues();
      setShowLeagueModal(false);
      setError('');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to save league');
    }
  };

  const deleteLeague = async (id: string) => {
    if (!confirm('Delete this league and all its formats?')) return;
    try {
      await api.delete(`/leagues/${id}`);
      fetchLeagues();
      setSuccess('League deleted');
    } catch {
      setError('Failed to delete league');
    }
  };

  // Format CRUD
  const openFormatModal = (leagueId: string, format: Format | null = null) => {
    setFormatLeagueId(leagueId);
    if (format) {
      setEditingFormat(format);
      setFormatForm({
        name: format.name,
        gameType: format.gameType,
        defaultLegs: format.defaultLegs,
        doubleIn: format.doubleIn,
        doubleOut: format.doubleOut
      });
    } else {
      setEditingFormat(null);
      setFormatForm({
        name: '',
        gameType: '501',
        defaultLegs: 5,
        doubleIn: false,
        doubleOut: true
      });
    }
    setShowFormatModal(true);
  };

  const handleFormatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingFormat) {
        await api.put(`/leagues/formats/${editingFormat.id}`, formatForm);
        setSuccess('Format updated');
      } else {
        await api.post(`/leagues/${formatLeagueId}/formats`, formatForm);
        setSuccess('Format created');
      }
      fetchLeagues();
      setShowFormatModal(false);
      setError('');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to save format');
    }
  };

  const deleteFormat = async (id: string) => {
    if (!confirm('Delete this format?')) return;
    try {
      await api.delete(`/leagues/formats/${id}`);
      fetchLeagues();
      setSuccess('Format deleted');
    } catch {
      setError('Failed to delete format');
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Leagues & Formats</h1>
        <button
          onClick={() => openLeagueModal()}
          className="btn-primary"
        >
          + Add League
        </button>
      </div>

      {error && (
        <div className="bg-red-900/50 text-red-300 px-4 py-3 rounded-lg border border-red-800">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-900/50 text-green-300 px-4 py-3 rounded-lg border border-green-800">
          {success}
        </div>
      )}

      {leagues.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-400 mb-4">No leagues set up yet</p>
          <button
            onClick={() => openLeagueModal()}
            className="text-[#1111d4] hover:text-[#1414f0] hover:underline"
          >
            Create your first league
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {leagues.map((league) => (
            <div key={league.id} className="card p-0 overflow-hidden">
              {/* League Header */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#252550]"
                onClick={() => toggleLeague(league.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedLeagues.has(league.id) ? (
                    <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                  )}
                  <div>
                    <h3 className="text-white font-semibold">{league.name}</h3>
                    {league.abbreviation && (
                      <span className="text-gray-400 text-sm">({league.abbreviation})</span>
                    )}
                  </div>
                  <span className="text-gray-500 text-sm ml-2">
                    {league.formats?.length || 0} format{league.formats?.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => openFormatModal(league.id)}
                    className="px-3 py-1 bg-[#1111d4] hover:bg-[#1414f0] text-white text-sm rounded transition-colors"
                  >
                    + Format
                  </button>
                  <button
                    onClick={() => openLeagueModal(league)}
                    className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteLeague(league.id)}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Formats List */}
              {expandedLeagues.has(league.id) && (
                <div className="border-t border-[#2a2a4a]">
                  {league.formats?.length === 0 ? (
                    <p className="text-gray-500 text-sm p-4 pl-12">No formats yet</p>
                  ) : (
                    <div className="divide-y divide-[#2a2a4a]">
                      {league.formats?.map((format) => (
                        <div
                          key={format.id}
                          className="flex items-center justify-between p-4 pl-12 bg-[#1a1a3a]"
                        >
                          <div>
                            <p className="text-white">{format.name}</p>
                            <p className="text-gray-400 text-sm">
                              {format.gameType} | {format.defaultLegs} legs |
                              {format.doubleIn ? ' Double In' : ' Single In'} |
                              {format.doubleOut ? ' Double Out' : ' Single Out'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => openFormatModal(league.id, format)}
                              className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => deleteFormat(format.id)}
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* League Modal */}
      {showLeagueModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setShowLeagueModal(false)}>
          <div className="bg-[#1a1a3a] border border-[#2a2a4a] rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-white mb-4">
              {editingLeague ? 'Edit League' : 'Add League'}
            </h2>
            <form onSubmit={handleLeagueSubmit} className="space-y-4">
              <div>
                <label className="label">League Name *</label>
                <input
                  type="text"
                  value={leagueForm.name}
                  onChange={(e) => setLeagueForm({ ...leagueForm, name: e.target.value })}
                  className="input"
                  placeholder="e.g., Strictly Come Darting"
                  required
                />
              </div>
              <div>
                <label className="label">Abbreviation</label>
                <input
                  type="text"
                  value={leagueForm.abbreviation}
                  onChange={(e) => setLeagueForm({ ...leagueForm, abbreviation: e.target.value })}
                  className="input"
                  placeholder="e.g., SCD"
                  maxLength={20}
                />
              </div>
              <div className="border-t border-[#2a2a4a] pt-4">
                <p className="text-gray-400 text-sm mb-3">Social Links (optional)</p>
                <div className="space-y-3">
                  <input
                    type="url"
                    value={leagueForm.facebookUrl}
                    onChange={(e) => setLeagueForm({ ...leagueForm, facebookUrl: e.target.value })}
                    className="input"
                    placeholder="Facebook group URL"
                  />
                  <input
                    type="url"
                    value={leagueForm.whatsappUrl}
                    onChange={(e) => setLeagueForm({ ...leagueForm, whatsappUrl: e.target.value })}
                    className="input"
                    placeholder="WhatsApp group URL"
                  />
                  <input
                    type="url"
                    value={leagueForm.discordUrl}
                    onChange={(e) => setLeagueForm({ ...leagueForm, discordUrl: e.target.value })}
                    className="input"
                    placeholder="Discord invite URL"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowLeagueModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingLeague ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Format Modal */}
      {showFormatModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setShowFormatModal(false)}>
          <div className="bg-[#1a1a3a] border border-[#2a2a4a] rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-white mb-4">
              {editingFormat ? 'Edit Format' : 'Add Format'}
            </h2>
            <form onSubmit={handleFormatSubmit} className="space-y-4">
              <div>
                <label className="label">Format Name *</label>
                <input
                  type="text"
                  value={formatForm.name}
                  onChange={(e) => setFormatForm({ ...formatForm, name: e.target.value })}
                  className="input"
                  placeholder="e.g., 501 Best of 7"
                  required
                />
              </div>
              <div>
                <label className="label">Game Type</label>
                <select
                  value={formatForm.gameType}
                  onChange={(e) => setFormatForm({ ...formatForm, gameType: e.target.value })}
                  className="input"
                >
                  {GAME_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Default Legs</label>
                <select
                  value={formatForm.defaultLegs}
                  onChange={(e) => setFormatForm({ ...formatForm, defaultLegs: parseInt(e.target.value) })}
                  className="input"
                >
                  {[...Array(21)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formatForm.doubleIn}
                    onChange={(e) => setFormatForm({ ...formatForm, doubleIn: e.target.checked })}
                    className="rounded bg-[#1a1a3a] border-[#2a2a4a] w-5 h-5"
                  />
                  <span>Double In</span>
                </label>
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formatForm.doubleOut}
                    onChange={(e) => setFormatForm({ ...formatForm, doubleOut: e.target.checked })}
                    className="rounded bg-[#1a1a3a] border-[#2a2a4a] w-5 h-5"
                  />
                  <span>Double Out</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowFormatModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingFormat ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
